import { tr } from '@/lib/i18n-core';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { useState, type KeyboardEvent } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarClock, Coins, FileText, ListChecks, Package } from 'lucide-react';
import { PACKAGE_KINDS, isEnrolmentKind, packageKindHasAllowance, packageKindNeedsValidity, type PackageKind, type PackagePricing } from '@his/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { money } from '@/lib/utils';
import { DataTable } from '@/components/ui/layout';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Field, FormSkeleton, Input, Select, Switch, Textarea } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TaxCategorySelect } from '@/components/TaxCategorySelect';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { packageKindLabel, type PackageQuote, type ServicePackage } from './PackagesTab';

interface CatalogueItem {
  id: string;
  code: string;
  name: string;
  basePriceMinor: number;
  category?: string;
}

function toPackageForm(pkg: ServicePackage | null) {
  return {
    code: pkg?.code ?? '',
    name: pkg?.name ?? '',
    kind: (pkg?.kind ?? 'invoice_bundle') as PackageKind,
    pricing: (pkg?.pricing ?? 'manual') as PackagePricing,
    price: pkg ? formatMoneyInput(pkg.priceMinor) : '0',
    validityDays: pkg?.validityDays ? String(pkg.validityDays) : '',
    allowance: pkg?.allowanceMinor ? formatMoneyInput(pkg.allowanceMinor) : '',
    branchId: pkg?.branchId ?? '',
    taxCategory: pkg?.saleChargeItem?.taxCategory ?? '',
    isActive: pkg?.isActive ?? true,
    nphiesClaimable: pkg?.nphiesClaimable ?? false,
    inclusionsNote: pkg?.inclusionsNote ?? '',
    exclusionsNote: pkg?.exclusionsNote ?? '',
    billOverrun: pkg?.billOverrun ?? true,
    items: Object.fromEntries((pkg?.items ?? []).map((item) => [item.chargeItem.id, String(item.qty)])) as Record<string, string>,
  };
}

function keepEnterInCell(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key === 'Enter') event.preventDefault();
}

export function PackagePage() {
  const { id } = useParams<{ id: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/billing/charges?tab=packages');

  const packages = useQuery({
    queryKey: ['packages'],
    queryFn: () => get<ServicePackage[]>('/packages'),
    enabled: Boolean(id),
  });

  if (!id) return <PackageForm key="new" pkg={null} returnTo={returnTo} />;
  if (packages.isLoading) return <FormSkeleton />;
  const pkg = packages.data?.find((row) => row.id === id);
  if (!pkg) return <Alert variant="destructive">{tr('billing', 'that_record_could_not_be_opened')}</Alert>;
  return <PackageForm key={pkg.id} pkg={pkg} returnTo={returnTo} />;
}

function PackageForm({ pkg, returnTo }: { pkg: ServicePackage | null; returnTo: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { hasModule, user } = useAuth();
  const hasInsurance = hasModule('insurance');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(() => toPackageForm(pkg));
  const [search, setSearch] = useState('');
  const sold = isEnrolmentKind(form.kind);
  const needsValidity = packageKindNeedsValidity(form.kind);
  const validityDays = Number(form.validityDays);
  const validityProblem = needsValidity && (!Number.isInteger(validityDays) || validityDays < 1 || validityDays > 3650);
  const hasAllowance = packageKindHasAllowance(form.kind);
  const allowanceMinor = hasAllowance ? minorFromInput(form.allowance) : 0;

  const charges = useQuery({
    queryKey: ['charge-items', 'for-package', search],
    queryFn: () => get<{ data: CatalogueItem[] }>(`/charge-items${qs({ q: search || undefined, limit: 200 })}`),
  });

  const packageId = pkg?.id ?? '';
  const nphiesQuote = useQuery({
    queryKey: ['package-quote', packageId, 'nphies'],
    queryFn: () => get<PackageQuote>(`/packages/${packageId}/quote${qs({ nphies: 1 })}`),
    enabled: hasInsurance && form.nphiesClaimable && Boolean(packageId),
    retry: false,
  });

  const known = new Map<string, number>();
  for (const item of pkg?.items ?? []) known.set(item.chargeItem.id, item.chargeItem.basePriceMinor);
  for (const item of charges.data?.data ?? []) known.set(item.id, item.basePriceMinor);

  const chosen = Object.entries(form.items).filter(([, qty]) => Number(qty) > 0);
  const contentsMinor = chosen.reduce((sum, [chargeItemId, qty]) => sum + Math.round((known.get(chargeItemId) ?? 0) * Number(qty)), 0);
  const pricing: PackagePricing = sold ? 'manual' : form.pricing;
  const priceMinor = pricing === 'sum' ? contentsMinor : minorFromInput(form.price);
  const fractional = chosen.some(([, qty]) => !Number.isInteger(Number(qty)));
  const serviceRows = (charges.data?.data ?? []).filter((row) => row.category !== 'package');

  const save = useMutation({
    mutationFn: () =>
      post('/packages', {
        ...(pkg ? { id: pkg.id } : {}),
        code: form.code,
        name: form.name,
        kind: form.kind,
        pricing,
        priceMinor: pricing === 'sum' ? 0 : minorFromInput(form.price),
        validityDays: needsValidity ? validityDays : null,
        allowanceMinor: hasAllowance && allowanceMinor > 0 ? allowanceMinor : null,
        branchId: form.branchId || null,
        ...(sold ? { taxCategory: form.taxCategory || null } : {}),
        isActive: form.isActive,
        nphiesClaimable: hasInsurance ? form.nphiesClaimable : (pkg?.nphiesClaimable ?? false),
        inclusionsNote: form.inclusionsNote || undefined,
        exclusionsNote: form.exclusionsNote || undefined,
        billOverrun: form.billOverrun,
        items: chosen.map(([chargeItemId, qty]) => ({ chargeItemId, qty: Number(qty) })),
      }),
    onSuccess: () => {
      toast.success(pkg ? tr('billing', 'package_updated') : tr('billing', 'package_added'));
      void queryClient.invalidateQueries({ queryKey: ['packages'] });
      void queryClient.invalidateQueries({ queryKey: ['package-quote'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const missing: string[] = [];
  if (form.code.trim().length < 2) missing.push(tr('billing', 'a_code_of_at_least_two_characters'));
  if (form.name.trim().length < 2) missing.push(tr('billing', 'a_name'));
  if (chosen.length === 0) missing.push(tr('billing', 'at_least_one_service_in_the_package'));
  if (sold && !(priceMinor > 0)) missing.push(tr('billing', 'the_price_the_patient_pays'));
  if (validityProblem) missing.push(tr('billing', 'how_many_days_it_can_be_used'));
  const overPriced = !sold && pricing === 'manual' && chosen.length > 0 && priceMinor > contentsMinor;
  const blocked = missing.length > 0 || overPriced || fractional || Number.isNaN(priceMinor) || Number.isNaN(allowanceMinor) || allowanceMinor < 0;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(toPackageForm(pkg));
  const branches = user?.branches ?? [];

  return (
    <FormScreen
      wide
      title={pkg ? tr('billing', 'edit_package') : tr('billing', 'add_package')}
      description={tr('billing', 'one_fixed_price_covering_a_set_of_services')}
      eyebrow={tr('billing', 'charge_master')}
      icon={Package}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (blocked) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('billing', 'package')} icon={Package}>
        <Field label={tr('billing', 'code')} required>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} disabled={Boolean(pkg)} />
        </Field>
        <Field label={tr('billing', 'name')} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={tr('billing', 'package_kind')} hint={tr('billing', `package_kind_hint_${form.kind}`)} className="sm:col-span-2">
          <ChoiceButtons<PackageKind>
            layout="chips"
            value={form.kind}
            disabled={Boolean(pkg)}
            onChange={(kind) => setForm({ ...form, kind })}
            options={PACKAGE_KINDS.map((kind) => ({ value: kind, label: packageKindLabel(kind) }))}
          />
        </Field>
        <Field label={tr('billing', 'offered_at')} hint={tr('billing', 'offered_at_hint')}>
          <Select value={form.branchId} onChange={(e) => setForm({ ...form, branchId: e.target.value })}>
            <option value="">{tr('billing', 'all_branches')}</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('billing', 'status')}>
          <Switch checked={form.isActive} onCheckedChange={(checked) => setForm({ ...form, isActive: checked })} label={tr('billing', 'offered_for_sale')} />
        </Field>
      </FormSection>

      <FormSection title={tr('billing', 'pricing')} icon={Coins}>
        {!sold && (
          <Field
            label={tr('billing', 'pricing')}
            hint={form.pricing === 'sum' ? tr('billing', 'the_price_follows_the_services_in_it_at_their') : tr('billing', 'a_package_can_cost_less_than_its_services_never')}
          >
            <ChoiceButtons<PackagePricing>
              value={form.pricing}
              onChange={(pricing) => setForm({ ...form, pricing })}
              options={[
                { value: 'manual', label: tr('billing', 'fixed_price') },
                { value: 'sum', label: tr('billing', 'sum_of_the_services') },
              ]}
            />
          </Field>
        )}
        <Field label={tr('billing', 'package_price')} required={pricing === 'manual'} hint={sold ? tr('billing', 'package_price_hint_sold') : undefined}>
          {pricing === 'sum' ? (
            <Input type="text" dir="ltr" className="tabular" value={money(contentsMinor)} readOnly disabled />
          ) : (
            <Input type="text" dir="ltr" inputMode="decimal" className="tabular" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
          )}
        </Field>
        {sold && <TaxCategorySelect value={form.taxCategory} onChange={(taxCategory) => setForm({ ...form, taxCategory })} />}
        {hasAllowance && (
          <Field label={tr('billing', 'medicines_allowance')} hint={tr('billing', 'medicines_allowance_hint')}>
            <Input
              type="text"
              dir="ltr"
              inputMode="decimal"
              className="tabular"
              placeholder={formatMoneyInput(0)}
              value={form.allowance}
              onChange={(e) => setForm({ ...form, allowance: e.target.value })}
            />
          </Field>
        )}

        <div className="flex items-center justify-between gap-3 rounded-md bg-muted/50 px-3 py-2 text-sm sm:col-span-2">
          <span className="text-muted-foreground">{tr('billing', 'contents_at_list_prices')}</span>
          <span className="tabular font-medium">{money(contentsMinor)}</span>
        </div>
        {overPriced && (
          <div className="sm:col-span-2">
            <Alert variant="destructive">{tr('billing', 'a_package_can_cost_less_than_its_services_never')}</Alert>
          </div>
        )}
        {sold && pkg?.saleChargeItem && (
          <p className="text-xs text-muted-foreground sm:col-span-2">{tr('billing', 'billed_as_service_x', { code: pkg.saleChargeItem.code })}</p>
        )}

        {sold ? (
          <Field label={tr('billing', 'beyond_included')} hint={tr('billing', 'beyond_included_hint')} className="sm:col-span-2">
            <ChoiceButtons
              layout="cards"
              value={form.billOverrun ? 'bill' : 'refuse'}
              onChange={(value) => setForm({ ...form, billOverrun: value === 'bill' })}
              options={[
                { value: 'bill', label: tr('billing', 'bill_it_normally') },
                { value: 'refuse', label: tr('billing', 'refuse_it_at_the_counter') },
              ]}
            />
          </Field>
        ) : (
          <Field label={tr('billing', 'anything_beyond_the_package')} hint={tr('billing', 'what_happens_when_a_stay_runs_longer_than_the')} className="sm:col-span-2">
            <ChoiceButtons
              layout="cards"
              value={form.billOverrun ? 'bill' : 'absorb'}
              onChange={(value) => setForm({ ...form, billOverrun: value === 'bill' })}
              options={[
                { value: 'bill', label: tr('billing', 'bill_it_on_top') },
                { value: 'absorb', label: tr('billing', 'absorb_it_the_package_price_is_final') },
              ]}
            />
          </Field>
        )}

        {hasInsurance && (
          <Field label={tr('billing', 'nphies')} hint={sold ? tr('billing', 'package_claim_hint') : tr('billing', 'priced_through_the_nphies_price_list_a_service')}>
            <div className="space-y-1">
              <Switch
                checked={form.nphiesClaimable}
                onCheckedChange={(checked) => setForm({ ...form, nphiesClaimable: checked })}
                label={sold ? tr('billing', 'claim_the_package_price_from_the_insurer') : tr('billing', 'claimable_through_nphies')}
              />
              {form.nphiesClaimable && !sold && (
                <p className="text-xs text-muted-foreground">
                  {!pkg
                    ? tr('billing', 'save_the_package_first_to_see_its_quote')
                    : nphiesQuote.data
                      ? `${tr('billing', 'nphies_quote')}: ${money(nphiesQuote.data.totals.netMinor)}`
                      : nphiesQuote.isError
                        ? describeError(nphiesQuote.error)
                        : '…'}
                </p>
              )}
            </div>
          </Field>
        )}
      </FormSection>

      {needsValidity && (
        <FormSection title={tr('billing', 'validity')} icon={CalendarClock}>
          <Field label={tr('billing', 'validity_days')} hint={tr('billing', 'validity_days_hint')} required>
            <Input type="number" min={1} max={3650} step={1} className="tabular" value={form.validityDays} onChange={(e) => setForm({ ...form, validityDays: e.target.value })} />
          </Field>
        </FormSection>
      )}

      <FormSection title={tr('billing', 'what_is_included')} icon={FileText}>
        <Field label={tr('billing', 'what_is_included')} className="sm:col-span-2">
          <Textarea rows={2} value={form.inclusionsNote} onChange={(e) => setForm({ ...form, inclusionsNote: e.target.value })} />
        </Field>
        <Field label={tr('billing', 'what_is_not_included')} className="sm:col-span-2">
          <Textarea rows={2} value={form.exclusionsNote} onChange={(e) => setForm({ ...form, exclusionsNote: e.target.value })} />
        </Field>
      </FormSection>

      <FormSection
        title={tr('billing', 'services_in_the_package')}
        description={sold ? tr('billing', `package_items_hint_${form.kind}`) : undefined}
        icon={ListChecks}
        columns={1}
      >
        <div className="flex flex-wrap items-center gap-2">
          {chosen.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {chosen.length} {tr('billing', 'chosen_list_value')} {money(contentsMinor)}
            </span>
          )}
          <Input
            className="ms-auto w-full sm:w-56"
            placeholder={tr('billing', 'search_services')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={keepEnterInCell}
          />
        </div>
        {fractional && <Alert variant="warning">{tr('billing', 'whole_units_only')}</Alert>}

        <DataTable
          rows={serviceRows}
          loading={charges.isLoading} refreshing={charges.isFetching}
          error={charges.error}
          rowKey={(row) => row.id}
          empty={tr('billing', 'no_services_match')}
          columns={[
            { header: tr('billing', 'code'), width: '120px', cell: (row) => <span className="font-mono text-xs">{row.code}</span> },
            { header: tr('billing', 'service'), cell: (row) => row.name },
            { header: tr('billing', 'list_price'), align: 'right', cell: (row) => money(row.basePriceMinor) },
            {
              header: sold ? tr('billing', 'included_quantity') : tr('billing', 'quantity'),
              width: '120px',
              cell: (row) => (
                <Input
                  type="number"
                  step={1}
                  min={0}
                  className="tabular"
                  placeholder="0"
                  value={form.items[row.id] ?? ''}
                  onKeyDown={keepEnterInCell}
                  onChange={(e) => setForm({ ...form, items: { ...form.items, [row.id]: e.target.value } })}
                />
              ),
            },
          ]}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={pkg ? tr('billing', 'save_package') : tr('billing', 'create_package')}
        loading={save.isPending}
        incomplete={blocked}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('billing', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
